import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import EditIcon from "@mui/icons-material/Edit";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  StatusEnum,
  statusColors,
  statusLabels,
} from "@/features/goods-receipts";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { apiOutletClient, formatDate, getErrorMessage } from "@/features/shared";
import { SupplierSelect } from "@/features/suppliers";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const GoodsReceipts = () => {
  const { outlet, changeOutlet, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [goodsReceipts, setGoodsReceipts] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const prevOutletRoleRef = useRef();

  // Parse seller from URL params
  const seller = useMemo(() => {
    const sellerParam = searchParams.get("seller");
    if (!sellerParam) return null;
    try {
      return JSON.parse(sellerParam);
    } catch {
      return null;
    }
  }, [searchParams]);

  const loadGoodReceipts = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("goods-receipts", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { keyword }),
          sellerableId: seller?.id || null,
          withSeller: true,
          withCreator: true,
        },
      });
      setGoodsReceipts(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat penerimaan barang"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    keyword,
    setRowCount,
    seller,
    showSnackbar,
  ]);

  useEffect(() => {
    loadGoodReceipts();
  }, [loadGoodReceipts]);

  // Reset seller when outlet role changes
  useEffect(() => {
    if (
      prevOutletRoleRef.current !== undefined &&
      prevOutletRoleRef.current !== outlet?.role
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("seller");
        return newParams;
      });
    }
    prevOutletRoleRef.current = outlet?.role;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet?.role]);

  const handleSellerChange = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(
          "seller",
          JSON.stringify({ id: value.id, name: value.name })
        );
      } else {
        newParams.delete("seller");
      }
      resetParams.forEach((param) => newParams.delete(param));
      return newParams;
    });
  };

  const toolbarContent = hasPermission(PermissionEnum.CREATE_GOODS_RECEIPTS) && (
        <Button variant="contained" component={Link} to="create">
          Tambah
        </Button>
      );

  return (
    <PageContainer
      breadcrumbs={[]}
      slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            {outlet?.role === OutletRoleEnum.HUB ? (
              <SupplierSelect
                value={seller}
                onChange={handleSellerChange}
                TextFieldProps={{
                  label: "Pemasok (Supplier)",
                }}
              />
            ) : (
              <AllOutletsSelect
                value={seller}
                onChange={handleSellerChange}
                allowedRoles={[OutletRoleEnum.HUB]}
                TextFieldProps={{
                  label: "Pemasok (Hub)",
                }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Pembeli",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari No. referensi / No. Surat Jalan"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={goodsReceipts}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor #",
              flex: 1,
              sortable: false,
            },
            {
              field: "sellerReferenceNumber",
              headerName: "No. Surat Jalan",
              flex: 1.5,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              valueGetter: (transactionDate) => formatDate(transactionDate),
            },
            {
              field: "creator",
              headerName: "Dibuat Oleh",
              flex: 1,
              sortable: false,
              valueGetter: (creator) => creator?.name ?? "-",
            },
            {
              field: "seller",
              headerName: "Pemasok",
              flex: 1.5,
              sortable: false,
              valueGetter: (seller) => seller.name,
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 1,
              sortable: false,
              valueGetter: (note) => note ?? "-",
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: ({ row: { referenceNumber, status } }) => {
                const actions = [];
                const isEditable =
                  status === StatusEnum.RECEIVED ||
                  status === StatusEnum.PARTIALLY_INVOICED;
                if (hasPermission(PermissionEnum.READ_GOODS_RECEIPTS)) {
                  actions.push(
                    <IconButton
                      key="view"
                      onClick={() => navigate(referenceNumber)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  );
                }

                if (
                  isEditable &&
                  hasPermission(PermissionEnum.READ_GOODS_RECEIPTS)
                ) {
                  actions.push(
                    <IconButton
                      key="edit"
                      onClick={() => navigate(`${referenceNumber}/edit`)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  );
                }

                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
